import { ErrorCode } from '@siteflow/shared';
import { clearSession, getAccessToken, setAccessToken } from './auth-store';
import { BASE_PATH } from './base-path';

export const API_BASE = `${BASE_PATH}/api/v1`;

/**
 * Absolute URL for an API path. The dashboard is always same-origin with the API
 * (the dev server proxies `/api`), but `Request` requires an absolute URL outside
 * a document context, so resolve against the current origin explicitly.
 */
function resolveUrl(path: string): string {
  const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
  return new URL(`${API_BASE}${path}`, origin).toString();
}

export interface ApiErrorEnvelope {
  code: string;
  message: string;
  status: number;
  details?: unknown;
  hint?: string;
  requestId?: string;
}

/** Typed view of the API's `{ error: { … } }` envelope. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly hint?: string;
  readonly requestId?: string;

  constructor(envelope: ApiErrorEnvelope) {
    super(envelope.message);
    this.name = 'ApiError';
    this.code = envelope.code;
    this.status = envelope.status;
    this.details = envelope.details;
    this.hint = envelope.hint;
    this.requestId = envelope.requestId;
  }

  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }

  /** `details` shaped like zod's flattened field errors, when present. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    const details = this.details;
    if (!details || typeof details !== 'object') return out;

    const issues = (details as { issues?: unknown }).issues ?? details;
    if (Array.isArray(issues)) {
      for (const issue of issues) {
        if (!issue || typeof issue !== 'object') continue;
        const path = (issue as { path?: unknown }).path;
        const message = (issue as { message?: unknown }).message;
        const key = Array.isArray(path) ? path.join('.') : undefined;
        if (key && typeof message === 'string' && !out[key]) out[key] = message;
      }
    }
    return out;
  }
}

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
  /** Serialised as JSON with the right content-type. */
  json?: unknown;
  body?: BodyInit | null;
  /** Skips the Authorization header (auth endpoints that must run unauthenticated). */
  anonymous?: boolean;
  /** Internal: prevents a retry loop after a refresh. */
  _retried?: boolean;
}

type AuthFailureHandler = (reason: string) => void;

function defaultAuthFailure(): void {
  if (typeof window === 'undefined') return;
  const { pathname, search } = window.location;
  if (pathname.startsWith('/login') || pathname.startsWith('/register')) return;
  const redirect = encodeURIComponent(`${pathname}${search}`);
  window.location.assign(`/login?redirect=${redirect}`);
}

let onAuthFailure: AuthFailureHandler = defaultAuthFailure;

/** Lets the router (and tests) own the "kicked out" transition. */
export function setAuthFailureHandler(handler: AuthFailureHandler | null): void {
  onAuthFailure = handler ?? defaultAuthFailure;
}

/** In-flight refresh, shared by every concurrent 401 so only one fires. */
let refreshInFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  const response = await fetch(resolveUrl('/auth/refresh'), {
    method: 'POST',
    credentials: 'include',
    headers: { accept: 'application/json' },
  });

  if (!response.ok) return false;

  const payload = (await response.json().catch(() => null)) as {
    accessToken?: string;
  } | null;

  if (!payload?.accessToken) return false;
  setAccessToken(payload.accessToken);
  return true;
}

/** Single-flight refresh. Concurrent callers await the same request. */
export function refreshAccessToken(): Promise<boolean> {
  refreshInFlight ??= performRefresh()
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

/** Test-only: drops any in-flight refresh so cases stay isolated. */
export function resetApiClient(): void {
  refreshInFlight = null;
}

function isExpiredTokenError(error: ApiError): boolean {
  return (
    error.status === 401 &&
    (error.code === ErrorCode.AUTH_TOKEN_EXPIRED || error.code === ErrorCode.AUTH_TOKEN_INVALID)
  );
}

async function toApiError(response: Response): Promise<ApiError> {
  const payload = (await response.json().catch(() => null)) as {
    error?: Partial<ApiErrorEnvelope>;
  } | null;

  const envelope = payload?.error;
  return new ApiError({
    code: envelope?.code ?? ErrorCode.INTERNAL_ERROR,
    message: envelope?.message ?? response.statusText ?? 'Request failed',
    status: envelope?.status ?? response.status,
    details: envelope?.details,
    hint: envelope?.hint,
    requestId: envelope?.requestId,
  });
}

function buildRequest(path: string, init: ApiRequestInit): Request {
  const { json, anonymous, _retried: _ignored, headers, ...rest } = init;
  void _ignored;

  const merged = new Headers(headers);
  merged.set('accept', 'application/json');

  let body = rest.body ?? null;
  if (json !== undefined) {
    merged.set('content-type', 'application/json');
    body = JSON.stringify(json);
  }

  if (!anonymous) {
    const token = getAccessToken();
    if (token) merged.set('authorization', `Bearer ${token}`);
  }

  return new Request(resolveUrl(path), {
    ...rest,
    body,
    headers: merged,
    credentials: 'include',
  });
}

/**
 * Fetches from the SiteFlow API.
 *
 * On a 401 carrying `AUTH_TOKEN_EXPIRED` it performs a single-flight
 * cookie-based refresh and replays the request exactly once. If the refresh
 * fails the session is cleared and the caller is sent to `/login?redirect=…`.
 */
export async function apiFetch<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const response = await fetch(buildRequest(path, init));

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  const error = await toApiError(response);

  if (error.code === ErrorCode.AUTH_REFRESH_REUSE) {
    clearSession();
    onAuthFailure(error.code);
    throw error;
  }

  if (isExpiredTokenError(error) && !init._retried && !init.anonymous) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      clearSession();
      onAuthFailure(error.code);
      throw error;
    }
    return apiFetch<T>(path, { ...init, _retried: true });
  }

  throw error;
}

export const api = {
  get: <T>(path: string, init?: ApiRequestInit) => apiFetch<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, json?: unknown, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'POST', json }),
  patch: <T>(path: string, json?: unknown, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'PATCH', json }),
  put: <T>(path: string, json?: unknown, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'PUT', json }),
  delete: <T>(path: string, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'DELETE' }),
};

/** Human copy for the error codes the dashboard surfaces inline. */
export function messageForError(error: unknown, fallback = 'Something went wrong.'): string {
  if (!ApiError.is(error)) return error instanceof Error ? error.message : fallback;

  switch (error.code) {
    case ErrorCode.AUTH_INVALID_CREDENTIALS:
      return 'That email and password combination is not recognised.';
    case ErrorCode.AUTH_ACCOUNT_LOCKED:
      return 'This account is temporarily locked after too many failed sign-in attempts. Try again in a few minutes or reset your password.';
    case ErrorCode.RATE_LIMITED:
      return 'Too many attempts. Wait a moment before trying again.';
    case ErrorCode.PERMISSION_DENIED:
      return 'You do not have permission to do that.';
    case ErrorCode.ORG_ACCESS_DENIED:
      return 'You no longer have access to that organisation.';
    case ErrorCode.VALIDATION_FAILED:
      return error.message || 'Some of those details are not valid.';
    case ErrorCode.CONFLICT:
      return error.message || 'That conflicts with something that already exists.';
    case ErrorCode.NOT_FOUND:
      return error.message || 'We could not find that.';
    case ErrorCode.INVITATION_INVALID:
      return 'This invitation link is no longer valid. Ask an admin to send a new one.';
    default:
      return error.message || fallback;
  }
}
